/**
 * Shared helpers for the tool-use renderers — value coercion,
 * truncation, path relativization, and tool-result content
 * normalization.
 *
 * These were previously private to the monolithic
 * :mod:`ToolUseBlockView`; pulling them out lets the per-family
 * summary modules and the body renderer reuse the same primitives
 * without circular imports.
 */

import type { ContentBlock, ToolResultBlock } from '@/lib/agent/types';

export function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

export function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

export function truncate(value: string | undefined, limit: number): string {
  if (!value) return '';
  return value.length > limit ? `${value.slice(0, limit - 1)}…` : value;
}

/**
 * Compact a path with no project context. Used as the fallback when
 * we don't yet know which project the chat is bound to (e.g. inside
 * Storybook or before ``cwd`` propagates).
 */
export function compactPath(path: string): string {
  if (!path) return '';
  const segs = path.split('/').filter(Boolean);
  if (segs.length <= 3) return path;
  return `…/${segs.slice(-3).join('/')}`;
}

/**
 * Format an absolute filesystem path for display, preferring a path
 * relative to the active project's ``cwd`` so the chat reads as a
 * conversation inside one project rather than a parade of identical
 * absolute prefixes.
 */
export function relativizePath(path: string, cwd: string | null): string {
  if (!path) return '';
  if (!path.startsWith('/')) return path;
  if (!cwd?.startsWith('/')) return compactPath(path);
  const normCwd = cwd.replace(/\/+$/, '');
  if (path === normCwd) return '.';
  if (path.startsWith(`${normCwd}/`)) {
    return path.slice(normCwd.length + 1);
  }
  const cwdParent = normCwd.slice(0, normCwd.lastIndexOf('/'));
  if (cwdParent && (path === cwdParent || path.startsWith(`${cwdParent}/`))) {
    const rest = path === cwdParent ? '' : path.slice(cwdParent.length + 1);
    return rest ? `../${rest}` : '..';
  }
  return compactPath(path);
}

/**
 * Last segment of a path — ``packages/webapp/src/main.tsx`` →
 * ``main.tsx``. The tool row leads with just the basename; the
 * full path lives in the expanded body.
 */
export function basename(path: string): string {
  if (!path) return '';
  const trimmed = path.replace(/\/+$/, '');
  const last = trimmed.split('/').pop();
  return last ?? trimmed;
}

/**
 * First non-flag token of a shell command — ``python -m pytest -k``
 * → ``python``. Used as a graceful fallback for the Bash row when
 * the model didn't supply a ``description``.
 */
export function commandProgram(command: string): string | null {
  if (!command) return null;
  for (const raw of command.trim().split(/\s+/)) {
    if (!raw || raw.startsWith('-')) continue;
    if (raw.includes('=')) continue;
    if (/^[A-Z_][A-Z0-9_]*=/.test(raw)) continue;
    if (raw === 'sudo' || raw === 'env' || raw === 'time') continue;
    const segs = raw.split('/');
    return segs[segs.length - 1] || raw;
  }
  return null;
}

/**
 * Compact a shell command while preserving enough arguments to be useful.
 *
 * Used for Monitor rows where the command is usually the key context
 * (``tail -f logs/server.log``) and showing only the program would be
 * too terse.
 */
export function compactShellCommand(command: string, limit: number): string {
  const normalized = command.trim().replace(/\s+/g, ' ');
  if (normalized.length <= limit) return normalized;

  const compacted = normalized
    .split(' ')
    .map((token) => compactCommandToken(token))
    .join(' ');
  return compacted.length <= limit ? compacted : `${compacted.slice(0, limit - 1)}…`;
}

function compactCommandToken(token: string): string {
  const match = token.match(/^(['"]?)(.*?)(['"]?)$/);
  if (!match) return token;
  const [, open, body, close] = match;
  if (!body.includes('/') || body.length < 28) return token;
  return `${open}${compactPath(body)}${close}`;
}

/**
 * Whether a tool result has any non-media textual content. Image-
 * and document-only results are already shown in the always-visible
 * media row above the expanded body.
 */
export function hasTextualContent(result: ToolResultBlock): boolean {
  const content = result.content;
  if (typeof content === 'string') return /\S/.test(content);
  if (Array.isArray(content)) {
    for (const piece of content) {
      if (normalizeMediaPiece(piece)) continue;
      const t = pieceToText(piece);
      if (t !== null && /\S/.test(t)) return true;
    }
    return false;
  }
  return true;
}

/**
 * Flatten a tool result to the textual payload a user needs to read.
 * Media pieces are rendered separately as thumbnails, so they are
 * intentionally skipped here.
 */
export function toolResultText(result: ToolResultBlock): string | null {
  const content = result.content;
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    const textual: string[] = [];
    for (const piece of content) {
      if (normalizeMediaPiece(piece)) continue;
      const text = pieceToText(piece);
      if (text !== null) textual.push(text);
    }
    return textual.length > 0 ? textual.join('\n\n') : null;
  }
  return JSON.stringify(content, null, 2);
}

/**
 * Collapse terminal carriage-return updates into the latest visible
 * transcript line.
 *
 * Progress reporters such as tqdm repeatedly write ``\r`` to return
 * to the start of the current line and overwrite it. Browser ``<pre>``
 * rendering treats those returns like new line-ish whitespace, so the
 * agent pane would show every intermediate progress state. This helper
 * treats each bare carriage return as a repaint of the current line
 * before rendering transcripts.
 */
export function normalizeTerminalOutput(text: string): string {
  const lines = [''];
  let col = 0;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (ch === '\r') {
      if (text[i + 1] === '\n') {
        lines.push('');
        col = 0;
        i += 1;
      } else {
        lines[lines.length - 1] = '';
        col = 0;
      }
      continue;
    }

    if (ch === '\n') {
      lines.push('');
      col = 0;
      continue;
    }

    if (ch === '\b') {
      if (col > 0) {
        const line = lines[lines.length - 1] ?? '';
        lines[lines.length - 1] = line.slice(0, col - 1) + line.slice(col);
        col -= 1;
      }
      continue;
    }

    const line = lines[lines.length - 1] ?? '';
    lines[lines.length - 1] = line.slice(0, col) + ch + line.slice(col + 1);
    col += 1;
  }

  return lines.map((line) => line.trimEnd()).join('\n');
}

export function pieceToText(piece: unknown): string | null {
  if (typeof piece !== 'object' || piece == null) return String(piece);
  const obj = piece as Record<string, unknown>;
  if (obj.block_type === 'TextBlock' && typeof obj.text === 'string') return obj.text;
  if ((obj.type === 'text' || typeof obj.text === 'string') && typeof obj.text === 'string') {
    return obj.text;
  }
  return JSON.stringify(piece);
}

export type NormalizedImage = {
  kind: 'image';
  block: Extract<ContentBlock, { block_type: 'ImageBlock' }>;
};
export type NormalizedDoc = {
  kind: 'document';
  block: Extract<ContentBlock, { block_type: 'DocumentBlock' }>;
};
export type NormalizedMedia = NormalizedImage | NormalizedDoc;

export function normalizeMediaPiece(piece: unknown): NormalizedMedia | null {
  if (typeof piece !== 'object' || piece == null) return null;
  const obj = piece as Record<string, unknown>;
  if (obj.block_type === 'ImageBlock') {
    return { kind: 'image', block: obj as unknown as NormalizedImage['block'] };
  }
  if (obj.block_type === 'DocumentBlock') {
    return { kind: 'document', block: obj as unknown as NormalizedDoc['block'] };
  }
  if (obj.type === 'image' && obj.source && typeof obj.source === 'object') {
    return {
      kind: 'image',
      block: {
        block_type: 'ImageBlock',
        source: obj.source as NormalizedImage['block']['source'],
        caption: typeof obj.caption === 'string' ? obj.caption : null,
      },
    };
  }
  if (obj.type === 'document' && obj.source && typeof obj.source === 'object') {
    return {
      kind: 'document',
      block: {
        block_type: 'DocumentBlock',
        source: obj.source as NormalizedDoc['block']['source'],
        title: typeof obj.title === 'string' ? obj.title : null,
        size: typeof obj.size === 'number' ? obj.size : null,
      },
    };
  }
  return null;
}

export function extractMediaPieces(result: ToolResultBlock): NormalizedMedia[] {
  const content = result.content;
  if (!Array.isArray(content)) return [];
  const out: NormalizedMedia[] = [];
  for (const piece of content) {
    const m = normalizeMediaPiece(piece);
    if (m) out.push(m);
  }
  return out;
}

/**
 * Pull out a flat list of tool names from a ``ToolSearch`` result.
 * The server emits tool references as JSON objects shaped like
 * ``{"type":"tool_reference","tool_name":"TodoWrite"}`` — sometimes
 * one-per-line, sometimes inside a JSON array, sometimes wrapped in
 * a TextBlock. Try each shape and gracefully fall back to the raw
 * string if nothing parses.
 */
export function extractToolReferences(result: ToolResultBlock): string[] {
  const names: string[] = [];
  const seen = new Set<string>();

  function consumeValue(value: unknown): void {
    if (value == null) return;
    if (typeof value === 'string') {
      consumeString(value);
      return;
    }
    if (Array.isArray(value)) {
      for (const v of value) consumeValue(v);
      return;
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      const tn = asString(obj.tool_name) ?? asString(obj.name);
      if (tn && !seen.has(tn)) {
        seen.add(tn);
        names.push(tn);
      }
    }
  }

  function consumeString(s: string): void {
    const trimmed = s.trim();
    if (!trimmed) return;
    try {
      consumeValue(JSON.parse(trimmed));
      return;
    } catch {
      // Not whole-JSON — try line-delimited objects below.
    }
    let parsedAny = false;
    for (const line of trimmed.split(/\r?\n/)) {
      const t = line.trim();
      if (!t) continue;
      try {
        consumeValue(JSON.parse(t));
        parsedAny = true;
      } catch {
        // skip non-JSON lines
      }
    }
    if (!parsedAny && !names.length) {
      names.push(trimmed);
      seen.add(trimmed);
    }
  }

  const content = result.content;
  if (typeof content === 'string') {
    consumeString(content);
  } else if (Array.isArray(content)) {
    for (const piece of content) {
      const t = pieceToText(piece);
      if (t !== null) consumeString(t);
    }
  }
  return names;
}

export interface HumanizedToolName {
  /** Display-ready primary tool name. Underscores collapsed to spaces for MCP tools. */
  display: string;
  /** Server label for MCP tools (``antioch-jupyter``). Undefined for built-ins. */
  server?: string;
  /** True iff the raw name matched the MCP ``mcp__<server>__<tool>`` convention. */
  isMcp: boolean;
  /** Original wire-format name -- still useful in tooltips for power users. */
  raw: string;
}

/**
 * Make a tool name fit to display in chat prose.
 *
 * MCP tools arrive from the SDK namespaced as ``mcp__<server>__<tool>``
 * (e.g. ``mcp__antioch-jupyter__list_kernels``). That raw form is
 * unreadable in a chat row and dominates the line. Split it into the
 * tool name (with snake_case collapsed to spaces) and the server, so
 * callers can render "**Called** list kernels via antioch-jupyter"
 * instead of "**Called** mcp__antioch-jupyter__list_kernels".
 *
 * Non-MCP names (``Bash``, ``Read``, ``TodoWrite``, etc.) pass through
 * unchanged -- those are already display-ready and have dedicated
 * family verbs / summaries.
 */
export function humanizeToolName(name: string | null | undefined): HumanizedToolName {
  const raw = name ?? '';
  if (!raw) return { display: '', isMcp: false, raw };
  const match = raw.match(/^mcp__(.+?)__(.+)$/);
  if (!match) return { display: raw, isMcp: false, raw };
  const [, server, tool] = match;
  // Snake/kebab to spaces. Two underscores in a row collapse to a
  // single space so e.g. ``run__tests`` (rare but legal) doesn't
  // produce a double space.
  const display = tool.replace(/[_-]+/g, ' ').trim();
  return {
    display: display || tool,
    server,
    isMcp: true,
    raw,
  };
}

/**
 * Format a millisecond budget as the most readable single unit:
 * ``500ms`` / ``45s`` / ``5m`` / ``1.5h``. Used by the Monitor row
 * where space is tight and the exact figure matters less than the
 * order of magnitude.
 */
export function formatTimeoutCompact(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  const h = ms / 3_600_000;
  return `${Number.isInteger(h) ? h.toFixed(0) : h.toFixed(1)}h`;
}
